import { isPreviewHost, isInternalSession } from "@/lib/session";
import { isBotClient, whenHuman } from "@/lib/botGuard";
/**
 * Microsoft Clarity loader.
 *
 * Clarity is a session-recording + heatmap tool. Unlike Meta Pixel we don't
 * need per-event helpers — Clarity automatically captures every click,
 * scroll, and route change from the moment its script loads.
 *
 * Safe no-op if REACT_APP_CLARITY_PROJECT_ID is unset OR the script has
 * already been injected in this page-load.
 *
 * View recordings + heatmaps at https://clarity.microsoft.com
 */

const PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;
let injected = false;

export function initClarity() {
  if (injected || !PROJECT_ID || typeof window === "undefined") return;
  // Oct 2026 — never record preview / localhost sessions: they are all
  // internal and were polluting Clarity's funnel + heatmaps.
  if (isPreviewHost() || isInternalSession()) return;
  // P4 — crawlers / webdriver never get recorded, and real browsers only
  // start recording once the human gate resolves (2s dwell or interaction).
  if (isBotClient()) return;
  injected = true;
  whenHuman().then(injectTag).catch(() => { injected = false; });
}

function injectTag() {
  // Wrapped in try/catch — Clarity's loader touches host-only APIs
  // (`window.webkit.messageHandlers` on iOS, native postMessage bridge
  // on Android) inside FB/IG in-app browsers. When the bridge object
  // has been torn down mid-recording it throws synchronously; without
  // this guard the exception bubbles into React's error boundary and
  // takes the whole page down. Loader failure is silent — we still
  // want the site working even if session replay isn't recording.
  try {
    // Verbatim from clarity.microsoft.com setup snippet — minimally wrapped
    // so we can pass a dynamic project ID from env.
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", PROJECT_ID);
  } catch (err) {
    injected = false;
    try { console.warn("[clarity] init suppressed", err && err.message); } catch { /* ignore */ }
  }
}

/**
 * Optional — identify the current logged-in user in Clarity so you can
 * search recordings by email or customer id.
 * Pass `null` on logout to clear the identity.
 */
export function identifyClarity(customUserId, customSessionId, customPageId, friendlyName) {
  if (typeof window === "undefined" || !window.clarity) return;
  try {
    window.clarity("identify", customUserId, customSessionId, customPageId, friendlyName);
  } catch { /* silent */ }
}

/** Make sure `window.clarity` exists as the official queueing stub so
 *  events fired BEFORE the tag script arrives (we load it post-idle) are
 *  replayed once it does. Identical shape to the snippet's own stub. */
function ensureStub() {
  if (typeof window === "undefined") return null;
  if (!window.clarity) {
    window.clarity = function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
  }
  return window.clarity;
}

/**
 * Fire a Clarity custom event + attach custom tags for the session.
 * Oct 2026 (P2 brief) — mirrors Meta's AddToCart / InitiateCheckout /
 * Purchase so Clarity's funnel can be reconciled against Meta + our DB.
 *
 * @param {string} name   e.g. "add_to_cart"
 * @param {object} tags   { key: value } — strings/numbers; set as Clarity
 *                        custom tags (searchable filters in the dashboard)
 */
export function clarityEvent(name, tags = {}) {
  if (!PROJECT_ID) return;
  const c = ensureStub();
  if (!c) return;
  try {
    for (const [k, v] of Object.entries(tags)) {
      if (v === undefined || v === null || v === "") continue;
      c("set", k, String(v).slice(0, 255));
    }
    c("event", name);
  } catch { /* silent */ }
}

/** Tag the current Clarity session as internal (admin). Clarity keeps the
 *  recording but it can be excluded in every dashboard with the filter
 *  `role is not admin`. Also fires an `internal_session` custom event. */
export function tagClarityInternal(email) {
  if (!PROJECT_ID) return;
  const c = ensureStub();
  if (!c) return;
  try {
    c("set", "role", "admin");
    c("set", "traffic", "internal");
    if (email) c("identify", email);
    c("event", "internal_session");
  } catch { /* silent */ }
}
